import { Injectable } from '@angular/core';

import { Observable, map } from 'rxjs';

import { GetAllLayersParams, LayerListHelperService, LayerService } from '@mirage-projects/cw-commons';
import { CatalogObjectSelection, Layer, ObjectBrowserFilter, ResultPage } from '@mirage-projects/cw-models';
import { JobPcvizService } from '@mirage-projects/cw-pcviz-core';

import { UploadWizardService } from '../../services/upload-wizard-service/upload-wizard.service';
import { ObjectBrowserSelectionService } from '../object-browser/selection.service';

/**
 * Service that has the logic of the existing layer component
 */
@Injectable({ providedIn: 'root' })
export class SelectExistingLayerService {
    constructor(
        private layerService: LayerService,
        private selectionService: ObjectBrowserSelectionService,
        private jobAdapterService: JobPcvizService,
        private uploadWizardService: UploadWizardService,
        private layerListHelperService: LayerListHelperService
    ) {}

    initializeFilters(isWizard = false): ObjectBrowserFilter[] {
        const filters = this.layerListHelperService.initFilters();
        return isWizard ? this.initializeWizardFilters(filters) : filters;
    }

    initializeWizardFilters(filters: ObjectBrowserFilter[]): ObjectBrowserFilter[] {
        // custom filtering for select existing layer in new layer wizard
        const layerTypeIndex = filters.findIndex((element: ObjectBrowserFilter) => element.fieldName === 'layerType');
        if (layerTypeIndex !== -1) {
            filters.splice(layerTypeIndex, 1);
        }

        filters.push({
            queryField: 'layer_type',
            value: this.jobAdapterService.getJobAdapter()?.getLayerType(),
        });
        if (this.jobAdapterService.getJobAdapter()?.getCategory()) {
            filters.push({
                queryField: 'category',
                value: this.jobAdapterService.getJobAdapter()?.getCategoryString(),
            });
        }

        return filters;
    }

    /**
     * Returns true if there are layers already selected
     */
    areLayersSelected(): boolean {
        return this.selectionService.getNumberSelectedElements() > 0;
    }

    /** load the layers from the server using the pagination and filter passed */
    loadLayers(params: Partial<GetAllLayersParams>): Observable<ResultPage<CatalogObjectSelection>> {
        return this.layerService.getAll(params).pipe(
            map((response: ResultPage<Layer>) => {
                if (response?.count < 1) {
                    return { ...response, results: [] };
                }

                const results = response.results.map((item: any) => {
                    const isSelected = this.selectionService.isElementSelected(item, 'layers');

                    return {
                        catalogObject: item,
                        selected: isSelected,
                    };
                });

                return { ...response, results };
            })
        );
    }

    getLayerTypeText(): string {
        if (this.uploadWizardService.isPointCloud()) {
            return 'LAYER_SELECT.OVERWRITE_DATA_POINTCLOUD';
        }
        if (this.uploadWizardService.isImageLayer()) {
            return 'LAYER_SELECT.OVERWRITE_DATA_VECTORS';
        }
        if (this.uploadWizardService.isModel3DLayer()) {
            return 'LAYER_SELECT.OVERWRITE_DATA_MODEL3D';
        }
        if (this.uploadWizardService.isFeatureLayer()) {
            return 'LAYER_SELECT.OVERWRITE_DATA_FEATURES';
        }
        if (this.uploadWizardService.isRaster()) {
            return 'LAYER_SELECT.OVERWRITE_DATA_RASTER';
        }

        return '';
    }

    getNameFormat(row: CatalogObjectSelection): string {
        return this.layerListHelperService.nameFormat(row);
    }

    getProjectsCount(row: CatalogObjectSelection): string {
        return this.layerListHelperService.formatProjectCount(row);
    }
}
